import { api } from '~/api';
import { getToken } from '~/auth/token';

export type AuthorizeResponse = {
    token: string;
    expires_at: number;
    refresh_token: string | null;
};

export function auth(token: string, accessToken: string): Promise<AuthorizeResponse> {
    return api.auth<AuthorizeResponse>('/authorize', {
        method: 'POST',
        body: { token },
        headers: { [ACCESS_TOKEN_HEADER]: accessToken },
    });
}

export function refresh(token: string | null): Promise<AuthorizeResponse> {
    return api<AuthorizeResponse>('/api/auth/refresh', {
        method: 'POST',
        body: { token },
        root: true,
    });
}

export type LoginResponse = {
    success: boolean;
    code_required: boolean;
};

export function login(email: string): Promise<LoginResponse> {
    return api.auth<LoginResponse>('/login', { method: 'POST', body: { email } });
}

export function logout(): Promise<boolean> {
    const token = getToken();
    return api.auth<boolean>('/logout', { method: 'POST', body: { token } });
}

export const ACCESS_TOKEN_HEADER = 'X-Access-Token';
export const LOGIN_TOKEN_HEADER = 'X-Login-Token';

export type Config2FAResponse = {
    required: boolean;
    configured: boolean;
};

export type Generate2FAResponse = {
    secret: string;
    url: string;
};

export type Verify2FAResponse = {
    codes: string[];
};

export type Validate2FAResponse = {
    token: string;
};

export type Validate2FARequest = {
    method: 'passcode' | 'authcode';
    passcode?: string;
    auth_code?: string;
};

export type RegenerateBackupCodesResponse = {
    codes: string[] | null;
};

export const twoFactorAuth = {
    config(loginToken?: string): Promise<Config2FAResponse> {
        return api.auth<Config2FAResponse>(`/2fa/config`, {
            includeToken: true,
            headers: loginToken ? { [LOGIN_TOKEN_HEADER]: loginToken } : {},
        });
    },

    generate(loginToken: string): Promise<Generate2FAResponse> {
        return api.auth<Generate2FAResponse>('/2fa/generate', {
            method: 'POST',
            includeToken: true,
            headers: {
                [LOGIN_TOKEN_HEADER]: loginToken,
            },
        });
    },

    verify(loginToken: string, passcode: string): Promise<Verify2FAResponse> {
        return api.auth<Verify2FAResponse>('/2fa/verify', {
            method: 'POST',
            includeToken: true,
            body: { passcode },
            headers: {
                [LOGIN_TOKEN_HEADER]: loginToken,
            },
        });
    },

    validate(loginToken: string, data: Validate2FARequest): Promise<Validate2FAResponse> {
        return api.auth<Validate2FAResponse>('/2fa/validate', {
            method: 'POST',
            includeToken: true,
            body: data,
            headers: {
                [LOGIN_TOKEN_HEADER]: loginToken,
            },
        });
    },

    disable(accessToken: string): Promise<boolean> {
        return api.auth<boolean>('/2fa/disable', {
            method: 'POST',
            includeToken: true,
            headers: {
                [ACCESS_TOKEN_HEADER]: accessToken,
            },
        });
    },

    regenerateBackupCodes(accessToken: string): Promise<RegenerateBackupCodesResponse> {
        return api.auth<RegenerateBackupCodesResponse>('/2fa/backup_codes', {
            method: 'POST',
            includeToken: true,
            headers: {
                [ACCESS_TOKEN_HEADER]: accessToken,
            },
        });
    },
};
